import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { MotifCheckbox } from "@ey-xd/motif-react";
import { Controller } from "react-hook-form";
import {
  formatAmountUnitDecimalPlace,
  formatAmountWithCommas,
} from "@/utility/common";

const useDefaultUnitDetails = () => {
  const { defaultUnit } = useSelector((state: any) => state.case);
  const decimalPlace = defaultUnit?.decimalPlace;
  const defaultUnitName = defaultUnit?.name;
  return { decimalPlace, defaultUnitName };
};

const CustomHeader = () => {
  const { defaultUnitName } = useDefaultUnitDetails();
  const unitName = defaultUnitName;
  return `(${unitName})`;
};

const amountCellRenderer = (field: string) => (params: any) => {
  const { decimalPlace, defaultUnitName } = useDefaultUnitDetails();
  const amount = params.data[field];
  const formattedDefaultAmount = formatAmountUnitDecimalPlace(
    amount,
    decimalPlace
  );
  return formattedDefaultAmount !== "-"
    ? `${formattedDefaultAmount} ${defaultUnitName}`
    : formattedDefaultAmount;
};
const accountUnitAmountRenderer =
  (field: string, unitDecimalPlace: any, accountUnit: any) => (params: any) => {
    const amount = params.data[field];
    let formattedAmount = amount;
    if (unitDecimalPlace !== null) {
      formattedAmount = formatAmountWithCommas(amount, unitDecimalPlace);
    }
    if (accountUnit) {
      formattedAmount += ` ${accountUnit}`;
    }
    return formattedAmount;
  };
export const useColumnCalimItemsDefs = (
  control: any,
  handleCheckboxChange: (id: string, checked: boolean) => void,
  unitDecimalPlace: any,
  accountUnit: any
) => {
  const { t } = useTranslation();

  return [
    {
      field: "claimItemReference",
      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.claimItemId"
      ),
      flex: 1,
      minWidth: 100,

      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
      },
      autoHeight: true,
      cellRenderer: function (params: any) {
        return (
          <Controller
            name="claimItems"
            control={control}
            render={({ field }) => {
              return (
                <MotifCheckbox
                  checked={field.value.includes(params.data.claimItemId)}
                  onChange={(e: any) => {
                    handleCheckboxChange(
                      params.data.claimItemId,
                      e.target.checked
                    );
                  }}
                  value={params.data.claimItemId}
                >
                  {params.data.claimItemReference}
                </MotifCheckbox>
              );
            }}
          />
        );
      },
    },
    {
      field: "category",
      minWidth: 100,

      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.category"
      ),
      flex: 1,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
      },
      autoHeight: true,
    },
    {
      field: "currentAmount",
      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.currentAmount",
        { unitName: CustomHeader() }
      ),
      minWidth: 180,

      flex: 1,
      cellRenderer: amountCellRenderer("currentAmount"),
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
      },
      autoHeight: true,
      headerHeight: 150,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
    },
    {
      field: "resolvedAmount",
      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.resolvedAmount",
        { unitName: CustomHeader() }
      ),
      minWidth: 180,

      flex: 1,
      cellRenderer: amountCellRenderer("resolvedAmount"),
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
      },
      autoHeight: true,
      headerHeight: 150,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
    },
    {
      field: "entitlementAmount",
      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.entitlement"
      ),
      minWidth: 180,

      flex: 1,
      autoHeight: true,
      headerHeight: 150,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
      cellRenderer: accountUnitAmountRenderer(
        "entitlementAmount",
        unitDecimalPlace,
        accountUnit
      ),
    },
    {
      field: "priorSettledAmount",
      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.priorSettled"
      ),
      minWidth: 180,

      flex: 1,
      headerHeight: 150,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
      cellRenderer: accountUnitAmountRenderer(
        "priorSettledAmount",
        unitDecimalPlace,
        accountUnit
      ),
    },
    {
      field: "settlementAmount",
      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.settlement"
      ),
      minWidth: 180,

      flex: 1,
      autoHeight: true,
      headerHeight: 150,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
      cellRenderer: accountUnitAmountRenderer(
        "settlementAmount",
        unitDecimalPlace,
        accountUnit
      ),
    },
  ];
};
